// Notification API queries - Based on Flutter v2_get_all_notifications_repo_impl.dart
import { useQuery } from '@tanstack/react-query';
import { api } from '../client';
import { API_ENDPOINTS } from '../endpoints';
import { authService } from '@/services/auth.service';
import type {
  GetAllNotificationsRequest,
  GetAllNotificationsResponse,
} from '@/features/notifications/types';

// Re-export types for consumers importing from this file
export type {
  NotificationFilter,
  NotificationData,
  Notification,
  GetAllNotificationsRequest,
  GetAllNotificationsResponse,
} from '@/features/notifications/types';

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (filters?: Record<string, unknown>) => [...notificationKeys.all, 'list', filters] as const,
};

// Fetch notifications with pagination
export const fetchNotifications = async (
  page: number = 1,
  pageSize: number = 10
): Promise<GetAllNotificationsResponse> => {
  // Always use userUId (coordinator's UID from JWT) for notifications filter
  const userUId = authService.getUserUid();

  if (!userUId) {
    throw new Error('User not authenticated');
  }

  const requestBody: GetAllNotificationsRequest = {
    page,
    pageSize,
    totalRecords: 0,
    suggestAutocompleteQuery: '',
    filters: [
      {
        fieldName: 'userUId',
        fieldValue: [userUId],
      },
    ],
  };

  const response = await api.notification.post<GetAllNotificationsResponse>(
    API_ENDPOINTS.notification.getAllNotifications,
    requestBody
  );

  return response.data;
};

// React Query hook for notifications with pagination
export const useNotifications = (page: number = 1, pageSize: number = 10, enabled: boolean = true) => {
  return useQuery({
    queryKey: notificationKeys.list({ page, pageSize }),
    queryFn: () => fetchNotifications(page, pageSize),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
    enabled, // Only fetch when enabled
  });
};

// Hook for limited notifications (for dialog popup)
export const useNotificationsPreview = (limit: number = 10) => {
  // Fetch first page with a large page size to get recent notifications
  const query = useNotifications(1, 10000);

  return {
    ...query,
    data: query.data
      ? {
          ...query.data,
          notificationsModelList: query.data.notificationsModelList?.slice(0, limit) || [],
          totalCount: query.data.totalRecords || query.data.notificationsModelList?.length || 0,
        }
      : undefined,
  };
};
